import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  ViewEncapsulation,
  Input,
} from '@angular/core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { Subject, combineLatest, Observable } from 'rxjs';
import { agGridLocale, PercentageCellRenderer } from 'app/utils/ag-grid.locale';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import { Store, select } from '@ngrx/store';
import { AppState } from 'app/reducers';
import * as _ from 'lodash';
import { PayrollRuleEntry } from 'app/api/payroll.rule.api.service';
import { PayrollRuleActions } from '../../actions';
import {
  PayrollSelectors,
  PayrollRuleSelectors,
  PayrollAnalyticsSelectors,
} from '../../selectors';
import { PayrollEntryByName } from 'app/api/payroll.api.service';
import { takeUntil } from 'rxjs/operators';
@Component({
  selector: 'app-payroll-analytics',
  templateUrl: './payroll-analytics.component.html',
  styleUrls: ['./payroll-analytics.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PayrollAnalyticsComponent implements OnInit, OnDestroy {
  public gridApi;
  public gridColumnApi;

  public columnDefs;
  public context;
  public defaultColDef;
  public statusBar;
  public localeText;
  public modules;

  rowData: any[];
  filteredColumns: any[];

  ana2Entries: { ana2Columns: any; ana2Rows: any };

  ana2Entries$: Observable<{ ana2Columns: any; ana2Rows: any }>;

  @Input() companyId: string;
  // tslint:disable-next-line: no-input-rename
  rules: PayrollRuleEntry[];

  // Private
  private _unsubscribeAll: Subject<any>;

  @ViewChild('agGridAna2PayAxis', { static: false }) agGrid: AgGridAngular;

  constructor(protected store: Store<AppState>) {
    this._unsubscribeAll = new Subject();
    this.modules = AllModules;
    this.localeText = agGridLocale.fr;
    this.columnDefs = this.getColumnDefs();
    this.defaultColDef = {
      enableValue: true,
      sortable: true,
      filter: true,
      resizable: true,
    };
    this.context = { componentParent: this };
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(PayrollSelectors.selectPayrollByNameUniq()),
      this.store.select(PayrollRuleSelectors.selectPayrollRuleByAxis('ana2')),
    ]).pipe(
      takeUntil(this._unsubscribeAll)
    ).subscribe(([payrollsUniqName, rules]) => {
      this.ana2Entries$ = this.store.select(
        PayrollAnalyticsSelectors.selectPayrollAna2Entries(
          payrollsUniqName,
          rules
        )
      );
    });

    this.ana2Entries$.pipe(
      takeUntil(this._unsubscribeAll)
    ).subscribe((ana2Entries) => {
      this.ana2Entries = ana2Entries;
      this.filteredColumns = this.ana2Entries.ana2Columns;
    });
  }

  // tslint:disable-next-line: typedef
  getColumnDefs() {
    return [{ headerName: 'Nom Prénom', field: 'name', pinned: 'left' }] as any;
  }

  onGridReady(params): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    this.rowData = this.ana2Entries.ana2Rows;

    const columnDefs = this.getColumnDefs();

    this.filteredColumns.forEach((column) => {
      columnDefs.push({
        headerName: column.name,
        field: column.name,
        editable: true,
        cellClass: ['ag-numeric-cell'],
        cellRenderer: PercentageCellRenderer,
        valueGetter: ({ data, colDef }) => {
          if (!data.hasOwnProperty('analytics')) {
            data.analytics = {};
          }

          if (data[colDef.field] && typeof data[colDef.field] === 'string') {
            data.analytics[colDef.field] = Number(data[colDef.field]);
          }
          return data.analytics ? data.analytics[colDef.field] : null;
        },
      });
    });

    // Show No affected value
    // columnDefs.push({
    //   headerName: 'N/A',
    //   field: 'not_affected',
    //   cellClass: ['ag-numeric-cell'],
    //   cellRenderer: PercentageCellRenderer,
    //   valueGetter: ({data}) => {
    //     if (!data.hasOwnProperty('analytics')) {
    //       return null;
    //     }
    //     const sumPercentAnalytics = _.sum(Object.values(data.analytics));
    //     return 100 - sumPercentAnalytics;
    //   }
    // });

    // Save validation
    columnDefs[0].valueGetter = ({ data }) => {
      data.validation = 0;
      let total = 0;
      if (data.analytics) {
        this.filteredColumns.forEach((column) => {
          if (data.analytics[column.name]) {
            total += data.analytics[column.name];
          }
        });
        data.validation = total || 0;
      }
      return data.name;
    };

    // check Cell Class
    columnDefs[0].cellClass = ({ data }) => {
      if (data.validation === 100) {
        return null;
      }
      return ['invalid-cell'];
    };

    // Show Total percentage
    // columnDefs.push({
    //   headerName: 'Total',
    //   field: 'validation',
    //   cellClass: ['ag-numeric-cell'],
    //   cellRenderer: PercentageCellRenderer
    // });

    this.columnDefs = columnDefs;
    this.gridApi.setColumnDefs(columnDefs);
    this.autoSizeAll(params);
  }

  onCellValueChanged(event): void {
    // check Cell Class for People
    // tslint:disable-next-line: no-shadowed-variable
    this.columnDefs[0].cellClass = ({ data }) => {
      if (data.validation === 100) {
        return [];
      }
      return ['invalid-cell'];
    };

    this.gridApi.setColumnDefs(this.columnDefs);

    const data: any = event.data;
    const input: PayrollRuleEntry = {} as PayrollRuleEntry;

    input.companyId = this.companyId;
    input.payrollExtraKey = event.column.colDef.field;
    input.payrollExtraValue = event.value || '0';
    input.payrollKey = data.matricule;
    input.axis = 'ana2';

    if (event.oldValue || String(event.oldValue) === '0') {
      input.key = null;
      this.store.dispatch(
        PayrollRuleActions.payrollRuleUpdateOneRequested({ input })
      );
    } else {
      console.log('Create Rule', input);
      this.store.dispatch(
        PayrollRuleActions.payrollRuleCreateRequested({ input })
      );
    }
  }

  private autoSizeAll(params): void {
    const allColumnIds = params.columnApi
      .getAllColumns()
      .map((column) => column.colId);
    setTimeout(() => params.columnApi.autoSizeColumns(allColumnIds), 0);
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
